import React, { useState, useEffect } from "react";
import { apiClient, baseURL } from "../utils/const";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const Catalogue = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: "",
    image: null,
  });
  const [catalogues, setCatalogues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Build absolute image URL for a stored image and log it for debugging
  const resolveImageUrl = (imagePath) => {
    if (!imagePath) return null;
    if (typeof imagePath === "string" && imagePath.startsWith("http")) {
      // console.log("[Catalogue] image URL:", imagePath);
      return imagePath;
    }
    const filePart = imagePath.includes("/") ? imagePath : encodeURIComponent(imagePath);
    const url = `${baseURL}/gallery/${filePart}`;
    return url;
  };

  useEffect(() => {
    fetchCatalogues();
  }, []);

  const fetchCatalogues = async () => {
    try {
      const response = await apiClient.post(`${baseURL}/user_api/get_categories.php`);
      //   console.log(response.data);
      if (response.data.message === "radha") {
        setCatalogues(response.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching catalogues:", err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData((prev) => ({
        ...prev,
        image: file,
      }));

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      // Backend requires name and image_path (file). Server will manage final naming.
      formDataToSend.set("name", formData.name || "");
      if (formData.image) {
        formDataToSend.set("image_path", formData.image, formData.image.name);
      }

      // Debug dump: iterate entries (console.log(FormData) shows empty)
      for (const [k, v] of formDataToSend.entries()) {
        if (v instanceof File) {
          // console.log("FormData:", k, `${v.name} (${v.type}, ${v.size} bytes)`);
        } else {
          // console.log("FormData:", k, v);
        }
      }

      const response = await apiClient.post(
        `insert_catalogue_category.php`,
        formDataToSend,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      // console.log(response.data);
      if (response.data.message === "radha") {
        toast.success("Catalogue added successfully!");
        setFormData({
          name: "",
          image: null,
        });
        setImagePreview(null);
        fetchCatalogues();
      } else {
        setError(response.data.message || "Failed to add catalogue");
      }
    } catch (err) {
      console.error("Error adding catalogue:", err);
      setError("Network error while adding catalogue");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toISOString().split('T')[0];
  };

  return (
    <div className="page-wrapper">
      <div className="page-content">
        <div className="container-xxl">
          {/* Header */}
          <div className="row mb-1">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center bg-body p-2 rounded shadow-sm">
                <h2 className="mb-0 fw-bold">Catalogue • Management</h2>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Add Catalogue Card */}
            <div className="col-12 mb-1">
              <div
                className="card"
                style={{
                  borderRadius: 16,
                  padding: 16,
                  boxShadow: "0 4px 15px rgba(0,0,0,0.04)",
                  marginBottom: 16,
                }}
              >
                <div
                  style={{
                    fontWeight: 600,
                    color: "var(--bs-body-color)",
                    marginBottom: 12,
                  }}
                >
                  Add Catalogue Category
                </div>
                <form onSubmit={handleSubmit}>
                  <div
                    style={{
                      display: "flex",
                      gap: 20,
                      alignItems: "flex-end",
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, minWidth: 200 }}>
                      <label style={{ fontSize: 12, color: "var(--bs-secondary-color)" }}>
                        Catalogue Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="Enter catalogue name"
                        className="form-control"
                        style={{
                          borderRadius: 8,
                          border: "1px solid #cbd5e1",
                          padding: "8px 12px",
                        }}
                        required
                      />
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 200 }}>
                      <label style={{ fontSize: 12, color: "var(--bs-secondary-color)" }}>
                        Main Image
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="form-control"
                        style={{
                          borderRadius: 8,
                          border: "1px solid #cbd5e1",
                          padding: "8px 12px",
                        }}
                        required
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn"
                      style={{
                        backgroundColor: "#dc3545",
                        height: 40,
                        borderRadius: 8,
                        padding: "8px 16px",
                        color: "white",
                        border: "none",
                        fontSize: 12,
                      }}
                    >
                      {loading ? "Submitting..." : "Submit"}
                    </button>
                  </div>

                  {/* Image Preview */}
                  {imagePreview && (
                    <div style={{ marginTop: 16 }}>
                      <label style={{ fontSize: 12, color: "var(--bs-secondary-color)", marginBottom: 8, display: "block" }}>
                        Image Preview:
                      </label>
                      <img
                        src={imagePreview}
                        alt="Preview"
                        style={{
                          maxWidth: 200,
                          maxHeight: 150,
                          borderRadius: 8,
                          border: "1px solid #cbd5e1",
                        }}
                      />
                    </div>
                  )}

                  {error && (
                    <div
                      className="alert alert-danger mt-3"
                      role="alert"
                      style={{ fontSize: 12 }}
                    >
                      {error}
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Catalogue Categories Table */}
            <div className="col-12">
              <div
                className="card border-0 shadow-sm"
                style={{ borderRadius: "12px" }}
              >
                <div
                  className="card-header"
                  style={{ borderRadius: "12px 12px 0 0" }}
                >
                  <h5 className="card-title mb-0 fw-bold">Catalogue Categories</h5>
                </div>
                <div className="card-body p-2" style={{ height: '400px', overflowY: 'auto' }}>
                  {catalogues.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead className="table-light" style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'var(--bs-light)' }}>
                          <tr>
                            <th>ID</th>
                            <th>Catalogue Name</th>
                            <th>Image</th>
                            <th>Add Image</th>
                            <th>Status</th>
                            <th>Created Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {catalogues.map((catalogue, index) => (
                            <tr key={index}>
                              <td>{catalogue.id || "—"}</td>
                              <td>
                                <span className="fw-medium">
                                  {catalogue.name || "—"}
                                </span>
                              </td>
                              <td>
                                {catalogue.image_path ? (
                                  <img
                                    src={resolveImageUrl(catalogue.image_path)}
                                    alt={catalogue.name}
                                    style={{
                                      width: 50,
                                      height: 50,
                                      objectFit: "cover",
                                      borderRadius: 8,
                                      border: "1px solid #cbd5e1",
                                    }}
                                    onError={(e) => {
                                      e.target.style.display = "none";
                                    }}
                                  />
                                ) : (
                                  <span className="text-muted">No Image</span>
                                )}
                              </td>
                              <td>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => navigate(`/Catalogue/${catalogue.id}`)}
                                >
                                  Add Image
                                </button>
                              </td>
                              <td>
                                <span className={`badge ${catalogue.status === "1" ? "bg-success" : "bg-secondary"}`}>
                                  {catalogue.status === "1" ? "Active" : "Inactive"}
                                </span>
                              </td>
                              <td>{formatDate(catalogue.created_at)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <i className="fas fa-images text-muted fs-1 mb-3"></i>
                      <p className="text-muted mb-0">No catalogue categories found</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Catalogue;
